import { Injectable } from '@angular/core';

export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

export interface CacheMetadata {
  key: string;
  size: number;
  lastAccessed: number;
  hits: number;
}

@Injectable({
  providedIn: 'root'
})
export class OfflineCacheService {
  private readonly DB_NAME = 'vitaguide-offline-db';
  private readonly DB_VERSION = 1;
  private readonly STORES = {
    surveys: 'surveys',
    surveyStatus: 'survey-status',
    profile: 'profile',
    results: 'results',
    reports: 'reports',
    metadata: 'metadata'
  };

  private db: IDBDatabase | null = null;

  constructor() {
    this.initDatabase();
  }

  /**
   * Инициализация IndexedDB
   */
  private async initDatabase(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => {
        console.error('❌ IndexedDB: Failed to open database', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Создаём object stores
        Object.values(this.STORES).forEach(storeName => {
          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName, { keyPath: 'id' });
          }
        });
      };
    });
  }

  /**
   * Универсальный метод для сохранения данных
   */
  async set<T>(storeName: string, key: string, data: T, ttlMs: number = 24 * 60 * 60 * 1000): Promise<void> {
    await this.ensureDb();

    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttlMs
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put({ id: key, ...entry });

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error(`❌ Cache: Failed to save '${key}'`, request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Универсальный метод для получения данных
   */
  async get<T>(storeName: string, key: string): Promise<T | null> {
    await this.ensureDb();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(key);

      request.onsuccess = () => {
        const result = request.result as (CacheEntry<T> & { id: string }) | undefined;

        if (!result) {
          resolve(null);
          return;
        }

        // Проверяем срок действия
        if (Date.now() > result.expiresAt) {
          this.delete(storeName, key); // Удаляем expired данные
          resolve(null);
          return;
        }

        resolve(result.data);
      };

      request.onerror = () => {
        console.error(`❌ Cache: Failed to get '${key}'`, request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Удаление данных
   */
  async delete(storeName: string, key: string): Promise<void> {
    await this.ensureDb();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error(`❌ Cache: Failed to delete '${key}'`, request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Получить все ключи из store
   */
  async getAllKeys(storeName: string): Promise<string[]> {
    await this.ensureDb();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAllKeys();

      request.onsuccess = () => {
        resolve(request.result as string[]);
      };

      request.onerror = () => {
        console.error(`❌ Cache: Failed to get keys from '${storeName}'`, request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Очистка всего store
   */
  async clearStore(storeName: string): Promise<void> {
    await this.ensureDb();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error(`❌ Cache: Failed to clear '${storeName}'`, request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Очистка expired данных из всех stores
   */
  async clearExpired(): Promise<number> {
    await this.ensureDb();
    let deletedCount = 0;

    for (const storeName of Object.values(this.STORES)) {
      const keys = await this.getAllKeys(storeName);

      for (const key of keys) {
        const data = await this.get(storeName, key as string);
        if (data === null) {
          deletedCount++;
        }
      }
    }

    return deletedCount;
  }

  /**
   * Получить размер кеша (приблизительно)
   */
  async getCacheSize(): Promise<number> {
    await this.ensureDb();
    let totalSize = 0;

    for (const storeName of Object.values(this.STORES)) {
      const keys = await this.getAllKeys(storeName);
      totalSize += keys.length;
    }

    return totalSize;
  }

  /**
   * Проверка наличия БД
   */
  private async ensureDb(): Promise<void> {
    if (!this.db) {
      await this.initDatabase();
    }
  }

  // ===== Специализированные методы для удобства =====

  /**
   * Кеширование списка опросов
   */
  async cacheSurveys(surveys: any[]): Promise<void> {
    await this.set(this.STORES.surveys, 'list', surveys, 7 * 24 * 60 * 60 * 1000); // 7 дней
  }

  /**
   * Получение закешированных опросов
   */
  async getCachedSurveys(): Promise<any[] | null> {
    return this.get(this.STORES.surveys, 'list');
  }

  /**
   * Кеширование доступных опросов (с информацией о доступности)
   */
  async cacheAvailableSurveys(surveys: any[]): Promise<void> {
    await this.set(this.STORES.surveys, 'available', surveys, 60 * 60 * 1000); // 1 час
  }

  /**
   * Получение закешированных доступных опросов
   */
  async getCachedAvailableSurveys(): Promise<any[] | null> {
    return this.get(this.STORES.surveys, 'available');
  }

  /**
   * Кеширование статуса опросов
   */
  async cacheSurveyStatus(status: any): Promise<void> {
    await this.set(this.STORES.surveyStatus, 'current', status, 60 * 60 * 1000); // 1 час
  }

  /**
   * Получение закешированного статуса
   */
  async getCachedSurveyStatus(): Promise<any | null> {
    return this.get(this.STORES.surveyStatus, 'current');
  }

  /**
   * Кеширование профиля пользователя
   */
  async cacheProfile(profile: any): Promise<void> {
    await this.set(this.STORES.profile, 'current', profile, 24 * 60 * 60 * 1000); // 1 день
  }

  /**
   * Получение закешированного профиля
   */
  async getCachedProfile(): Promise<any | null> {
    return this.get(this.STORES.profile, 'current');
  }

  /**
   * Кеширование результатов
   */
  async cacheResults(results: any[]): Promise<void> {
    await this.set(this.STORES.results, 'list', results, 24 * 60 * 60 * 1000); // 1 день
  }

  /**
   * Получение закешированных результатов
   */
  async getCachedResults(): Promise<any[] | null> {
    return this.get(this.STORES.results, 'list');
  }

  /**
   * Кеширование отчетов
   */
  async cacheReports(reports: any[]): Promise<void> {
    await this.set(this.STORES.reports, 'list', reports, 24 * 60 * 60 * 1000); // 1 день
  }

  /**
   * Получение закешированных отчетов
   */
  async getCachedReports(): Promise<any[] | null> {
    return this.get(this.STORES.reports, 'list');
  }

  /**
   * Очистка всех данных пользователя (при logout)
   */
  async clearAllUserData(): Promise<void> {
    await Promise.all([
      this.clearStore(this.STORES.surveys),
      this.clearStore(this.STORES.surveyStatus),
      this.clearStore(this.STORES.profile),
      this.clearStore(this.STORES.results),
      this.clearStore(this.STORES.reports)
    ]);
  }
}
